import React, { useMemo, useState } from "react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Plus, Pencil, MapPin, Power, AlertTriangle, CalendarDays } from "lucide-react";
import { useTutteLeZone } from "../hooks/useZone";
import { createZona, updateZona, disattivaZona, riattivaZona } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

const DEFAULT_COLORS = [
  "#1B5E20", "#1565C0", "#F57C00", "#7B1FA2", "#C62828",
  "#5D4037", "#00897B", "#0097A7", "#5E35B1", "#D81B60",
];

const GIORNI_SERVIZIO = ["LUN", "MAR", "MER", "GIO", "VEN"];
const GIORNO_LABEL = {
  LUN: "Lunedì", MAR: "Martedì", MER: "Mercoledì",
  GIO: "Giovedì", VEN: "Venerdì",
};
const NONE_VAL = "_none_";

function extractErr(ex, fallback = "Errore") {
  const status = ex?.response?.status;
  const detail = ex?.response?.data?.detail;
  if (status === 403) return "Non hai i permessi per modificare le zone.";
  if (status === 409) return detail || "Esiste già una zona con questo nome.";
  if (typeof detail === "string") return detail;
  return fallback;
}

export default function ZoneSettings() {
  const { user } = useAuth();
  const { zone, loading, refresh } = useTutteLeZone();
  const [editZone, setEditZone] = useState(null); // null | "new" | zona obj
  const [confirmDisattiva, setConfirmDisattiva] = useState(null);
  const [filtro, setFiltro] = useState("tutte"); // tutte | attive | da_configurare | disattivate

  const canEdit = user && (user.ruolo === "AMMINISTRATORE" || user.ruolo === "UFFICIO");

  // Riepilogo
  const stats = useMemo(() => {
    const tot = zone.length;
    const attive = zone.filter((z) => z.attiva).length;
    const configurate = zone.filter((z) => z.attiva && z.giornoServizio).length;
    const daConfigurare = zone.filter((z) => z.attiva && !z.giornoServizio).length;
    return { tot, attive, configurate, daConfigurare };
  }, [zone]);

  const zoneVisibili = useMemo(() => {
    let list = [...zone];
    if (filtro === "attive") list = list.filter((z) => z.attiva);
    else if (filtro === "disattivate") list = list.filter((z) => !z.attiva);
    else if (filtro === "da_configurare") list = list.filter((z) => z.attiva && !z.giornoServizio);
    // Ordina: prima per ordinamento (null/0 in fondo), poi per nome
    list.sort((a, b) => {
      const oa = a.ordinamento ?? 9999;
      const ob = b.ordinamento ?? 9999;
      if (oa !== ob) return oa - ob;
      return (a.nome || "").localeCompare(b.nome || "");
    });
    return list;
  }, [zone, filtro]);

  const onDisattiva = async (z) => {
    if (z._nProduttori === 0) {
      try {
        await disattivaZona(z.id, null);
        toast.success("Zona disattivata");
        refresh();
      } catch (ex) {
        toast.error(extractErr(ex, "Errore disattivazione"));
      }
      return;
    }
    setConfirmDisattiva({ zona: z, sposta_in: "" });
  };

  const onConfirmDisattiva = async () => {
    if (!confirmDisattiva.sposta_in) {
      toast.error("Seleziona una zona di destinazione");
      return;
    }
    try {
      const res = await disattivaZona(confirmDisattiva.zona.id, confirmDisattiva.sposta_in);
      toast.success(`Zona disattivata · ${res.spostati} produttori spostati in ${res.destinazione}`);
      setConfirmDisattiva(null);
      refresh();
    } catch (ex) {
      toast.error(extractErr(ex));
    }
  };

  const onRiattiva = async (z) => {
    try {
      await riattivaZona(z.id);
      toast.success("Zona riattivata");
      refresh();
    } catch (ex) {
      toast.error(extractErr(ex));
    }
  };

  return (
    <div className="space-y-4" data-testid="zone-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
            Gestione zone
          </h1>
          <p className="text-slate-500 text-sm mt-1">Aree operative, giorno di servizio, ordinamento</p>
        </div>
        {canEdit && (
          <Button
            onClick={() => setEditZone("new")}
            className="bg-[#1B5E20] hover:bg-[#144818] text-white min-h-[44px]"
            data-testid="btn-new-zona"
          >
            <Plus size={16} className="mr-1" /> Nuova zona
          </Button>
        )}
      </div>

      {/* Riepilogo */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="zone-summary">
        <Card className="p-3" data-testid="stat-tot">
          <div className="text-xs uppercase tracking-wider text-slate-500">Zone totali</div>
          <div className="text-2xl font-bold text-slate-900">{stats.tot}</div>
        </Card>
        <Card className="p-3" data-testid="stat-attive">
          <div className="text-xs uppercase tracking-wider text-slate-500">Zone attive</div>
          <div className="text-2xl font-bold text-[#1B5E20]">{stats.attive}</div>
        </Card>
        <Card className="p-3" data-testid="stat-configurate">
          <div className="text-xs uppercase tracking-wider text-slate-500">Con giorno servizio</div>
          <div className="text-2xl font-bold text-[#1565C0]">{stats.configurate}</div>
        </Card>
        <Card
          className={`p-3 ${stats.daConfigurare > 0 ? "border-[#F57C00] border-2 bg-[#FFF8E1]" : ""}`}
          data-testid="stat-da-configurare"
        >
          <div className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1">
            {stats.daConfigurare > 0 && <AlertTriangle size={12} className="text-[#F57C00]" />}
            Da configurare
          </div>
          <div className={`text-2xl font-bold ${stats.daConfigurare > 0 ? "text-[#F57C00]" : "text-slate-400"}`}>
            {stats.daConfigurare}
          </div>
        </Card>
      </div>

      {stats.daConfigurare > 0 && (
        <Card className="p-3 bg-[#FFF8E1] border border-[#F57C00]/40" data-testid="banner-da-configurare">
          <div className="flex items-start gap-2 text-sm">
            <AlertTriangle size={18} className="text-[#F57C00] shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-slate-900">Queste zone non hanno ancora un giorno di servizio configurato.</div>
              <div className="text-slate-600 text-xs mt-0.5">
                Imposta il giornoServizio per ogni zona attiva, così la pianificazione e il giro funzioneranno correttamente.
              </div>
              <Button
                size="sm" variant="outline" className="mt-2 h-8 text-xs"
                onClick={() => setFiltro("da_configurare")}
                data-testid="filter-da-configurare-btn"
              >
                Mostra solo zone da configurare
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Filtri */}
      <div className="flex gap-2 flex-wrap" data-testid="zone-filters">
        {[
          { v: "tutte", label: `Tutte (${stats.tot})` },
          { v: "attive", label: `Attive (${stats.attive})` },
          { v: "da_configurare", label: `Da configurare (${stats.daConfigurare})` },
          { v: "disattivate", label: `Disattivate (${stats.tot - stats.attive})` },
        ].map((f) => (
          <button
            key={f.v}
            data-testid={`filter-${f.v}`}
            onClick={() => setFiltro(f.v)}
            className={`px-3 h-9 rounded-full text-xs font-medium border transition-colors ${
              filtro === f.v
                ? "bg-[#1B5E20] text-white border-[#1B5E20]"
                : "bg-white text-slate-700 border-slate-200 hover:border-[#1B5E20]/40"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-slate-500 p-4" data-testid="zone-loading">Caricamento…</div>
      ) : zoneVisibili.length === 0 ? (
        <Card className="p-8 text-center text-slate-500" data-testid="zone-empty">
          {zone.length === 0
            ? "Nessuna zona configurata. Crea la prima zona."
            : "Nessuna zona corrisponde al filtro."}
        </Card>
      ) : (
        <div className="grid gap-2">
          {zoneVisibili.map((z) => {
            const daConfig = z.attiva && !z.giornoServizio;
            return (
              <Card
                key={z.id}
                className={`p-4 ${z.attiva ? "" : "opacity-60"} ${daConfig ? "border-[#F57C00]/60 border-2" : ""}`}
                data-testid={`zona-card-${z.id}`}
              >
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="h-12 w-12 rounded-xl flex items-center justify-center text-white shrink-0"
                       style={{ backgroundColor: z.colore }}>
                    <MapPin size={20} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="font-semibold text-slate-900">{z.nome}</div>
                      {z.giornoServizio ? (
                        <Badge className="bg-[#1565C0] text-white text-[10px] flex items-center gap-1">
                          <CalendarDays size={10} /> {GIORNO_LABEL[z.giornoServizio]}
                        </Badge>
                      ) : z.attiva ? (
                        <Badge className="bg-[#F57C00] text-white text-[10px] flex items-center gap-1" data-testid={`badge-da-configurare-${z.id}`}>
                          <AlertTriangle size={10} /> Giorno da configurare
                        </Badge>
                      ) : null}
                      {z.ordinamento != null && (
                        <Badge variant="outline" className="text-[10px]">#{z.ordinamento}</Badge>
                      )}
                      {!z.attiva && <Badge className="bg-slate-400 text-white text-[10px]">DISATTIVATA</Badge>}
                    </div>
                    {z.descrizione && <div className="text-sm text-slate-500 mt-0.5">{z.descrizione}</div>}
                    {z.note && <div className="text-xs text-slate-500 mt-1 italic">{z.note}</div>}
                    <div className="text-xs text-slate-400 mt-1" data-testid={`zona-nprod-${z.id}`}>
                      {z._nProduttori ?? 0} produttori attivi
                    </div>
                  </div>
                  {canEdit && (
                    <div className="flex flex-col gap-1 shrink-0">
                      <Button size="sm" variant="outline" onClick={() => setEditZone(z)}
                              className="text-xs min-h-[36px]" data-testid={`edit-zona-${z.id}`}>
                        <Pencil size={12} className="mr-1" /> Modifica
                      </Button>
                      {z.attiva ? (
                        <Button size="sm" variant="outline" onClick={() => onDisattiva(z)}
                                className="text-xs min-h-[36px] text-[#C62828] border-[#C62828]/40 hover:bg-[#C62828]/10"
                                data-testid={`disattiva-zona-${z.id}`}>
                          <Power size={12} className="mr-1" /> Disattiva
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => onRiattiva(z)}
                                className="text-xs min-h-[36px] text-[#1B5E20] border-[#1B5E20]/40 hover:bg-[#1B5E20]/10"
                                data-testid={`riattiva-zona-${z.id}`}>
                          <Power size={12} className="mr-1" /> Riattiva
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {editZone && (
        <ZonaFormDialog
          zona={editZone === "new" ? null : editZone}
          onClose={() => setEditZone(null)}
          onSaved={() => { setEditZone(null); refresh(); }}
        />
      )}

      <Dialog open={!!confirmDisattiva} onOpenChange={(v) => !v && setConfirmDisattiva(null)}>
        <DialogContent data-testid="dialog-disattiva-zona">
          <DialogHeader>
            <DialogTitle>Impossibile disattivare la zona</DialogTitle>
            <DialogDescription>
              La zona <strong>{confirmDisattiva?.zona?.nome}</strong> ha {confirmDisattiva?.zona?._nProduttori} produttori
              attivi. Per disattivare la zona, sposta prima tutti i produttori in un&apos;altra zona attiva.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>Sposta tutti i produttori in:</Label>
            <Select
              value={confirmDisattiva?.sposta_in || ""}
              onValueChange={(v) => setConfirmDisattiva({ ...confirmDisattiva, sposta_in: v })}
            >
              <SelectTrigger data-testid="dz-target"><SelectValue placeholder="Seleziona zona di destinazione" /></SelectTrigger>
              <SelectContent>
                {zone.filter((z) => z.attiva && z.id !== confirmDisattiva?.zona?.id).map((z) => (
                  <SelectItem key={z.id} value={z.id}>{z.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDisattiva(null)}>Annulla</Button>
            <Button onClick={onConfirmDisattiva} className="bg-[#C62828] hover:bg-[#B71C1C] text-white" data-testid="dz-confirm">
              Sposta e disattiva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function ZonaFormDialog({ zona, onClose, onSaved }) {
  const isEdit = !!zona;
  const [form, setForm] = useState({
    nome: zona?.nome || "",
    colore: zona?.colore || DEFAULT_COLORS[0],
    descrizione: zona?.descrizione || "",
    attiva: zona?.attiva !== false,
    giornoServizio: zona?.giornoServizio || null,
    ordinamento: zona?.ordinamento ?? 0,
    note: zona?.note || "",
  });
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async () => {
    if (!form.nome.trim()) { toast.error("Nome obbligatorio"); return; }
    if (!/^#[0-9A-Fa-f]{6}$/.test(form.colore)) { toast.error("Colore in formato hex #RRGGBB"); return; }
    setSubmitting(true);
    try {
      const payload = {
        nome: form.nome.trim(),
        colore: form.colore.toUpperCase(),
        descrizione: form.descrizione || null,
        attiva: form.attiva,
        giornoServizio: form.giornoServizio || null,
        ordinamento: Number.isFinite(parseInt(form.ordinamento, 10)) ? parseInt(form.ordinamento, 10) : 0,
        note: form.note || "",
      };
      if (isEdit) {
        await updateZona(zona.id, payload);
        toast.success("Zona aggiornata");
      } else {
        await createZona(payload);
        toast.success("Zona creata");
      }
      onSaved();
    } catch (ex) {
      toast.error(extractErr(ex));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="dialog-zona-form">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Modifica zona: ${zona.nome}` : "Nuova zona"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Aggiorna nome, giorno servizio, ordinamento o note."
              : "Compila i campi per creare una nuova zona operativa."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Nome zona *</Label>
            <Input data-testid="zf-nome" value={form.nome}
                   onChange={(e) => setForm({ ...form, nome: e.target.value })}
                   placeholder="es. PISTOIA CENTRO" />
          </div>
          <div>
            <Label>Colore *</Label>
            <div className="flex items-center gap-2 flex-wrap">
              <Input data-testid="zf-color" type="color" value={form.colore}
                     onChange={(e) => setForm({ ...form, colore: e.target.value.toUpperCase() })} className="h-10 w-14 p-1" />
              <Input value={form.colore} onChange={(e) => setForm({ ...form, colore: e.target.value })}
                     className="h-10 w-32 font-mono uppercase" />
              <div className="flex gap-1 flex-wrap">
                {DEFAULT_COLORS.map((c) => (
                  <button key={c} type="button" onClick={() => setForm({ ...form, colore: c })}
                          className={`h-6 w-6 rounded-full border-2 ${form.colore.toUpperCase() === c.toUpperCase() ? "border-slate-900 scale-110" : "border-slate-200"}`}
                          style={{ backgroundColor: c }} />
                ))}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <Label>Giorno servizio</Label>
              <Select
                value={form.giornoServizio || NONE_VAL}
                onValueChange={(v) => setForm({ ...form, giornoServizio: v === NONE_VAL ? null : v })}
              >
                <SelectTrigger data-testid="zf-giorno"><SelectValue placeholder="Nessuno" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE_VAL}>— Da configurare —</SelectItem>
                  {GIORNI_SERVIZIO.map((g) => (
                    <SelectItem key={g} value={g}>{GIORNO_LABEL[g]} ({g})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Ordinamento</Label>
              <Input data-testid="zf-ord" type="number" min="0"
                     value={form.ordinamento}
                     onChange={(e) => setForm({ ...form, ordinamento: e.target.value })} />
            </div>
          </div>
          <div>
            <Label>Descrizione</Label>
            <Input data-testid="zf-descrizione" value={form.descrizione || ""}
                   onChange={(e) => setForm({ ...form, descrizione: e.target.value })}
                   placeholder="Opzionale" />
          </div>
          <div>
            <Label>Note</Label>
            <Textarea data-testid="zf-note" value={form.note || ""} rows={2}
                      onChange={(e) => setForm({ ...form, note: e.target.value })}
                      placeholder="Note interne, es. orari preferiti, vincoli operativi…" />
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={form.attiva} onCheckedChange={(v) => setForm({ ...form, attiva: v })} data-testid="zf-attiva" />
            <Label>Attiva</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={onSubmit} disabled={submitting}
                  className="bg-[#1B5E20] hover:bg-[#144818] text-white" data-testid="zf-submit">
            {submitting ? "Salvataggio..." : "Salva"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
